import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Inline, Stack, Text } from '@atlaskit/primitives';
import type { UpdateType } from '@app/app-updates/src/schema';
import { UpdateStatus } from '@app/app-updates-table/src/components/UpdateStatus';
import { UpdateChangeType } from '@app/app-updates-table/src/components/UpdateChangeType';
import { DateRangeInfo } from '@app/common';

const messages = defineMessages({
  rolloutSchedule: {
    id: 'appUpdatesDetails.changelog.rolloutSchedule',
    defaultMessage: 'Rollout schedule',
    description: 'Label for rollout schedule information',
  },
  status: {
    id: 'appUpdatesDetails.changelog.status',
    defaultMessage: 'Status',
    description: 'Label for update status information',
  },
  changeType: {
    id: 'appUpdatesDetails.changelog.changeType',
    defaultMessage: 'Change type',
    description: 'Label for update change type information',
  },
});

export interface AppUpdatesChangelogSummaryProps {
  /**
   * The status of the update (e.g., COMING_SOON, PLANNED, ROLLING_OUT, etc.)
   */
  status?: UpdateType['status'] | null;

  /**
   * The type of change (e.g., IMPROVEMENT, FIX, ANNOUNCEMENT)
   */
  type?: UpdateType['type'] | null;

  /**
   * ISO date string for when the release starts
   */
  releaseStartDate?: string | null;

  /**
   * ISO date string for when the release ends
   */
  releaseEndDate?: string | null;
}

/**
 * Displays a summary of changelog metadata including status, change type,
 * and release date information.
 *
 * Example:
 * ```tsx
 * <AppUpdatesChangelogSummary
 *   status="APPROVED"
 *   type="IMPROVEMENT"
 *   releaseStartDate="2024-03-01"
 *   releaseEndDate="2024-03-31"
 * />
 * ```
 */
export const AppUpdatesChangelogSummary: FunctionComponent<
  AppUpdatesChangelogSummaryProps
> = ({ status, type, releaseStartDate, releaseEndDate }) => {
  const { formatMessage } = useIntl();
  // Don't render if no data
  if (!status && !type && !releaseStartDate && !releaseEndDate) {
    return null;
  }

  return (
    <Stack>
      <Inline space="space.400">
        <Stack space="space.100">
          <Text size="small" weight="bold">
            {formatMessage(messages.status)}
          </Text>
          {status && <UpdateStatus status={status} />}
        </Stack>
        <Stack space="space.100">
          <Text size="small" weight="bold">
            {formatMessage(messages.changeType)}
          </Text>
          {type && <UpdateChangeType type={type} />}
        </Stack>
        <Stack space="space.100">
          <Text size="small" weight="bold">
            {formatMessage(messages.rolloutSchedule)}
          </Text>
          <DateRangeInfo
            startDate={releaseStartDate}
            endDate={releaseEndDate}
            status={status}
          />
        </Stack>
      </Inline>
    </Stack>
  );
};

export default AppUpdatesChangelogSummary;
